import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import moment from "moment";
import queryString from "query-string";
import _ from "lodash";

import ReactPaginate from "react-paginate";
import { STARTING_PAGE, DEBOUNCE_SEARCH } from "../../constants/constants";

import Spinner from "../../components/generics/Spinner";
import Filter from "../../components/generics/Filter";
import ToggleAlertLink from "../../components/generics/ToggleAlertLink";
import ListComparisonHeader from "../../components/generics/ListComparisonHeader";

import Alert from "../../utils/Alert";

import { getListPdf, fetchListCSV } from "../../store/priceLists/api";
import { fetchPricesListDetail, fetchPricesListDetailSuccess, fetchPricesListDetailFailure } from "../../store/priceLists/actions";
import { fetchPrices, fetchPricesSuccess, fetchPricesFailure } from "../../store/priceLists/actions";

import PricesList from "../../components/priceLists/PricesList";
import { withRouter } from "utils/WithRouter";

const PriceListDetailContainer = ({ history, match, location, fetchPricesListDetail, priceListDetail, fetchPrices, priceListPrices }) => {
  const [search, setSearch] = useState(queryString.parse(location.search).search);
  const [header, setHeader] = useState("");
  const [csvIsLoading, setCsvIsLoading] = useState(false);
  const [businessType, setBusinessType] = useState(undefined);
  const [pdfIsLoading, setPdfIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const alertRef = useRef(null);

  useEffect(() => {
    const params = queryString.parse(location.search);
    const page = params.page ? params.page : STARTING_PAGE;
    const search = params.search ? params.search : "";
    setBusinessType(params.businessType ? params.businessType : undefined);
    fetchPricesListDetail(match.params.id);
    fetchPrices(match.params.id, search, page);
  }, []);

  useEffect(() => {
    if (!priceListDetail.loading && priceListDetail.priceList) {
      const description = priceListDetail.priceList.description ? `${priceListDetail.priceList.description} - ` : "";
      setHeader(
        `${description}${priceListDetail.priceList.vendor_name} - ${moment(priceListDetail.priceList.date_created).utc().format("L")}`
      );
    }
  }, [priceListDetail]);

  useEffect(() => {
    debounceSearch();
    return debounceSearch.cancel;
  }, [page, search, businessType]);

  // ---------------------------------------------------------------------------
  // Filters
  // ---------------------------------------------------------------------------

  const onSearchChange = (event) => {
    const search = event.target.value ? event.target.value : "";
    setSearch(search);
    setPage(STARTING_PAGE);
  };

  const onBusinessTypeChange = (event) => {
    const businessType = event.target.value && event.target.value != "-1" ? event.target.value : undefined;
    setPage(STARTING_PAGE);
    setBusinessType(businessType);
  };

  const filterList = () => {
    let params = {};
    params.page = page;
    if (search) {
      params.search = search;
    }
    if (businessType) {
      params.businessType = businessType;
    }
    history.replace({
      search: queryString.stringify(params),
    });
    fetchPrices(match.params.id, search, businessType, page);
  };

  const debounceSearch = _.debounce(filterList, DEBOUNCE_SEARCH);

  const handlePageRequest = (data) => {
    let newPage = data.selected + 1;
    if (newPage !== page) {
      setPage(newPage);
    }
  };

  const loadCsv = () => {
    setCsvIsLoading(true);
    fetchListCSV(match.params.id, search, businessType).then((r) => {
      const blob = new Blob([r.data], { type: "text/csv" });
      let link = document.createElement("a");
      document.body.appendChild(link);
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute("download", header);
      link.click();
      document.body.removeChild(link);
      setCsvIsLoading(false);
    });
  };

  const printPdf = () => {
    setPdfIsLoading(true);
    getListPdf(match.params.id, search, businessType).then((r) => {
      let blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = header;
      link.click();
      URL.revokeObjectURL(url);
      setPdfIsLoading(false);
    });
  };

  return (
    <div className="animated fadeIn">
      <Alert ref={alertRef} />
      <div className="card">
        <ListComparisonHeader
          dark
          showAction={false}
          title={header}
          iconClass="fa fa-file-text-o mr-05"
          actionTitle="Export CSV"
          csvLoading={csvIsLoading}
          fromFile={true}
          loadCsv={loadCsv}
          pdfLoading={pdfIsLoading}
          printPdf={printPdf}
        />
        <Filter
          searchLabel2="Search"
          searchPlaceholder2="Search by description, brand or item code"
          searchVal2={search}
          onSearchChange2={onSearchChange}
          dropdownLabel="Filter by business type"
          dropdownPlaceholder="Select business type"
          dropdownVal={businessType}
          dropdownByElementId
          dropdownOptions={[
            { id: "business_type_mg", name: "MG" },
            { id: "business_type_restaurant", name: "Restaurant" },
            { id: "business_type_market", name: "Market" },
            { id: "business_type_pizzeria", name: "Pizzeria" },
          ]}
          onDropdownChange={onBusinessTypeChange}
          colClass="col-12 col-sm-3"
        />
      </div>
      {priceListDetail.loading ? (
        <Spinner />
      ) : (
        <div>
          {priceListDetail.priceList && (
            <ToggleAlertLink
              title={
                priceListDetail.priceList.items_with_error_amount > 0
                  ? `${priceListDetail.priceList.items_with_error_amount} items could not be processed in this list`
                  : "All items were succesfully processed"
              }
              iconClass={
                priceListDetail.priceList.items_with_error_amount > 0 ? "fa fa-exclamation-triangle mr-05" : "fa fa-check-circle mr-05"
              }
              actionLink={`/price_lists/${match.params.id}/errors?page=1`}
              actionTitle={"Show errors"}
              isError={priceListDetail.priceList.items_with_error_amount > 0}
            />
          )}
          {priceListPrices.loading ? (
            <Spinner />
          ) : (
            <div className="card">
              <div className="card-block p-0">
                <table className="table table-striped table-bordered datatable mb-0">
                  <thead>
                    <tr>
                      <th>Item code</th>
                      <th>Item name</th>
                      <th>Vendor description</th>
                      <th>Brand</th>
                      <th>Items per pack</th>
                      <th>Packs per case</th>
                      <th>Pack size</th>
                      <th>Original price</th>
                      <th>Original unit</th>
                      <th>Normalized price / unit</th>
                    </tr>
                  </thead>
                  <PricesList pricesList={priceListPrices.prices} />
                </table>
                {priceListPrices.pages > 1 && (
                  <ReactPaginate
                    pageCount={priceListPrices.pages}
                    pageRangeDisplayed={2}
                    marginPagesDisplayed={1}
                    previousLabel="<"
                    nextLabel=">"
                    onPageChange={handlePageRequest}
                    initialPage={page - 1}
                    disableInitialCallback={true}
                    containerClassName="pagination"
                    pageClassName="page-item"
                    pageLinkClassName="page-link"
                    activeClassName="page-item active"
                    previousClassName="paginate_button previous"
                    nextClassName="paginate_button next"
                    disabledClassName="page-item disabled"
                    forcePage={page - 1}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    priceListDetail: state.priceLists.priceListDetail,
    priceListPrices: state.priceLists.priceListPrices,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchPricesListDetail: (id) => {
      dispatch(fetchPricesListDetail(id)).payload.then(
        (response) => {
          dispatch(fetchPricesListDetailSuccess(response.data));
        },
        (error) => {
          dispatch(fetchPricesListDetailFailure(error));
        }
      );
    },
    fetchPrices: (id, search, businessType, page) => {
      dispatch(fetchPrices(id, search, businessType, page)).payload.then(
        (response) => {
          dispatch(fetchPricesSuccess(response.data));
        },
        (error) => {
          dispatch(fetchPricesFailure(error));
        }
      );
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(PriceListDetailContainer));
